<template>
    <div class="gallery-list-viewer">
        <section
            class="panel viewer-panel"
            ref="viewerPanel">
            <div class="panel-heading">
                <h2>
                    <router-link to="/galleries">Galleries</router-link>
                    <i class="clippie fa fa-info-circle" v-tooltip="'View artwork created by other students'"></i>
                    <span class="headerTags" style="font-size: 15px; color: #b0b0b0; margin-left: 1rem">{{headerTags}}</span>
                </h2>
            </div>
            <div class="panel-body">
                <div class="gallery-controls">
                    <router-link
                        to="new-gallery"
                        class="btn btn-success create-gallery"
                        v-if="canCreateGallery">
                        Create
                    </router-link>
                    <form @submit.prevent="forceSearch">
                        <input
                            class="form-control"
                            type="text"
                            placeholder="Search..."
                            v-model="searchText"
                            @keyup.enter="$event.target.blur()">
                    </form>
                    <div class="sort-controls">
                        <label>Sort by:</label>
                        <select
                            class="form-control"
                            v-model="searchParams.order">
                            <option
                                v-for="order in orderOptions"
                                :value="order.id">
                                {{ order.name }}
                            </option>
                        </select>
                    </div>
                    <button
                        class="btn btn-success"
                        type="button"
                        @click="toggleFilters">
                        {{ showFilters ? 'Hide filters' : 'Show filters'}}
                    </button>
                </div>

                <gallery-grid
                    ref="galleryGrid"
                    :gutter-width="0"
                    :count-per-row="galleriesPerRow"
                    :threshold-sizing="galleriesThresholdSizing"
                    :galleries="galleries"
                    @click="onGalleryClick"
                    v-show="showGalleries">
                </gallery-grid>

                <div
                    class="error-container"
                    v-show="showLoadingError">
                    <p>Error loading galleries.</p>
                </div>

                <div
                    class="loading-container"
                    v-show="showLoadingMessage">
                    <p>Loading...</p>
                </div>

                <div
                    class="no-galleries-container"
                    v-show="showNoGalleriesMessage">
                    <p>No galleries yet.</p>
                </div>
            </div>
        </section>

        <section
            class="panel filters-panel"
            v-if="showFilters">
            <div class="panel-heading">
                <h2>Filters</h2>
            </div>
            <div class="panel-body">
                <form>
                    <div class="form-group categories">
                        <label>Categories</label>
                        <select
                            :class="termSelectClasses"
                            v-model="searchParams.term">
                            <option value="">Term</option>
                            <option
                                v-for="term in termOptions"
                                :value="term.id">
                                {{ term.name }}
                            </option>
                        </select>

                        <select
                            :class="pathSelectClasses"
                            v-model="searchParams.path"
                            v-show="!isTermFoundation(searchParams.term)">
                            <option value="">Path</option>
                            <option
                                v-for="path in pathOptions"
                                :value="path.id">
                                {{ path.name }}
                            </option>
                        </select>

                        <select
                            :class="groupSelectClasses"
                            v-model.number="searchParams.group">
                            <option value="">Group</option>
                            <option
                                v-for="group in groupOptions"
                                :value="group.id">
                                {{ group.label }}
                            </option>
                        </select>

                        <select
                            :class="assignmentSelectClasses"
                            v-model="searchParams.condensedAssignment">
                            <option value="">Assignment</option>
                            <option
                                v-for="ca in condensedAssignmentOptions"
                                :value="ca.id">
                                {{ ca.name }}
                                ({{ ca.galleryCount }})
                            </option>
                        </select>
                    </div>

                    <div class="form-group author-group">
                        <label>Author</label>
                        <v-select
                            :options="authorOptions"
                            :class="authorSelectClasses"
                            v-model="searchParams.author"
                            @search="searchAuthors"
                            @keyup.native="handleEmptyAuthorSearch"
                            placeholder="Example">
                            <template slot="no-options">
                                Search authors...
                            </template>
                        </v-select>
                    </div>

                    <div class="form-group">
                        <label>Software Used</label>
                        <software-select
                            :options="gallerySoftware"
                            v-model="searchParams.software">
                        </software-select>
                    </div>

                    <div class="form-group tags">
                        <label>Tags</label>
                        <tag-select
                            :search="searchTags"
                            v-model="searchParams.tags">
                        </tag-select>
                    </div>

                    <div
                        class="form-group recruiter-visibility"
                        v-if="canViewHighlights">
                        <label>
                            <input
                                type="checkbox"
                                v-model="searchParams.highlightsOnly">
                            Show highlights only
                        </label>
                    </div>

                    <div class="form-group">
                        <button
                            type="button"
                            class="btn btn-success"
                            @click="clearFilters">
                            Clear
                        </button>
                    </div>
                </form>
            </div>
        </section>
    </div>
</template>

<script>
    import toastr from 'toastr';

    import Galleries      from '../services/galleries';
    import Noembed        from '../services/noembed';
    import Users          from '../services/users';
    import GalleryUtils   from '../utils/galleries';
    import RateUtils      from '../utils/rate';
    import SoftwareSelect from './reusable/software-select';
    import TagSelect      from './reusable/tag-select';
    import GalleryGrid    from './widgets/GalleryGrid';

    const { PaginatedGalleryList } = GalleryUtils;

    function getViewportBottom() {
        const $window = $(window);
        return $window.scrollTop() + $window.height();
    }

    function getBottomPos(elem) {
        const $elem = $(elem);
        return $elem.offset().top + $elem.outerHeight();
    }


    export default {
        components: {
            SoftwareSelect,
            TagSelect,
            GalleryGrid,
        },

        data() {
            return {
                loadingInitial: true,
                galleryList: new PaginatedGalleryList(),
                viewerPanelSize: 'lg', // lg | md
                searchParams: {
                    order: 'latest',
                    search: '', // use searchText; this is debounced from that
                    term: '',
                    path: '',
                    group: '',
                    //assignment: '', // use searchParams_assignments instead
                    condensedAssignment: '',
                    author: null, // uses v-select, so null is correct
                    software: [],
                    tags: [],
                    highlightsOnly: false,
                },
                searchTagsH: '',
                searchText: '',
                authorSearchResults: [],
                showFilters: false,
                galleryFilters: {
                    terms: [],
                    paths: [],
                    groups: [],
                    assignments: [],
                },
                gallerySoftware: [],
            };
        },

        created() {
            if (this.userRoles.includes('Recruiter')) {
                this.searchParams.highlightsOnly = true;
            }
        },

        async mounted() {
            this.loadingInitial = true;

            window.scrollTo(0, 0);

            // Load gallery filters; we'll need these to validate query string
            // parameters.
            await this.loadGalleryFilters();

            // Set the search parameters based on values found in the query
            // string.
            this.loadQueryStringParams();

            // Watch all the search parameters, so that we can update the query
            // string whenever they change.
            this.watchSyncQueryString();

            // Validate the search parameters after enabling query string syncing
            // so that invalid parameters are corrected.
            this.makeFiltersValid();

            // Load gallery info
            await Promise.all([
                this.loadGallerySoftware(),

                // When the author, software, and tags are set from values in the
                // query string, they're only given an id. In order to properly
                // render them, we'll need the other info, like names, icons,
                // etc.
                this.loadAuthorInFilter(),
                this.loadSoftwareInFilter(),
                this.loadTagsInFilter(),
            ]);

            // We need to update the gallery grid size and content when the
            // window is resized or scrolled.
            window.addEventListener('resize', this.onResize);
            window.addEventListener('scroll', this.onScroll);
            this.resizeGalleryGrid();

            // At this point, we can at least show the gallery controls. We'll
            // load galleries next.
            this.loadingInitial = false;

            // Load the gallery list.
            await this.reloadGalleryList();
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.onResize);
            window.removeEventListener('scroll', this.onScroll);
        },

        methods: {
            searchTags: Galleries.searchTags,

            async onResize() {
                this.resizeGalleryGrid();
                await this.autoLoadGalleries();
            },

            async onScroll() {
                await this.autoLoadGalleries();
            },

            onGalleryClick() {
                this.$store.dispatch('pushGalleryViewerContext', {
                    initial: this.galleries,
                    params: this.gallerySearchParams,
                });
            },

            loadQueryStringParams() {
                const qOrder = (v) => {
                    this.searchParams.order = v || 'latest';
                };
                const qSearch = (v) => {
                    this.searchParams.search = v;
                    this.searchText = v;
                };
                const qTerm = (v) => {
                    this.searchParams.term = v;
                };
                const qPath = (v) => {
                    this.searchParams.path = parseInt(v) || '';
                };
                const qAssignment = (v) => {
                    const vi = parseInt(v);
                    const cao = this.condensedAssignmentOptions.find(cao => {
                        return !!cao.assignments.find(ao => {
                            return ao.id === vi;
                        });
                    });
                    if (cao) {
                        this.searchParams.condensedAssignment = cao.id;
                    } else {
                        this.searchParams.condensedAssignment = '';
                    }
                };
                const qGroup = (v) => {
                    const vi = parseInt(v);
                    if (!isNaN(vi)) {
                        this.searchParams.group = vi;
                    } else {
                        this.searchParams.group = '';
                    }
                };
                const qAuthor = (v) => {
                    const vi = parseInt(v);
                    if (!isNaN(vi)) {
                        this.searchParams.author = { id: vi };
                    } else {
                        this.searchParams.author = null;
                    }
                };
                const qSoftware = (v) => {
                    this.searchParams.software = v.split(',').map(idStr => {
                        return {
                            id: parseInt(idStr),
                        };
                    }).filter(software => {
                        return !isNaN(software.id);
                    });
                };
                const qTags = (v) => {
                    this.searchParams.tags = v.split(',').map(idStr => {
                        return {
                            id: parseInt(idStr),
                        };
                    }).filter(tag => {
                        return !isNaN(tag.id);
                    });
                };
                const loadQueryParam = (name, cb) => {
                    cb(this.$route.query[name] || '');
                };
                loadQueryParam('order',      qOrder);
                loadQueryParam('search',     qSearch);
                loadQueryParam('term',       qTerm);
                loadQueryParam('path',       qPath);
                loadQueryParam('assignment', qAssignment);
                loadQueryParam('group',      qGroup);
                loadQueryParam('author',     qAuthor);
                loadQueryParam('software',   qSoftware);
                loadQueryParam('tags',       qTags);
            },

            watchSyncQueryString() {
                const qOrder = () => {
                    if (this.searchParams.order !== 'latest') {
                        return this.searchParams.order;
                    } else {
                        return null;
                    }
                };
                const qSearch = () => {
                    return this.searchParams.search || null;
                };
                const qTerm = () => {
                    return this.searchParams.term || null;
                };
                const qPath = () => {
                    return this.searchParams.path || null;
                };
                const qAssignment = () => {
                    if (this.searchParams_assignments.length > 0) {
                        return this.searchParams_assignments[0];
                    } else {
                        return null;
                    }
                };
                const qGroup = () => {
                    return this.searchParams.group || null;
                };
                const qAuthor = () => {
                    if (this.searchParams.author) {
                        return this.searchParams.author.id;
                    } else {
                        return null;
                    }
                };
                const qSoftware = () => {
                    if (this.searchParams.software.length > 0) {
                        return this.searchParams.software.map(s => {
                            return s.id;
                        }).join(',');
                    } else {
                        return null;
                    }
                };
                const qTags = () => {
                    if (this.searchParams.tags.length > 0) {
                        return this.searchParams.tags.map(t => {
                            return t.id;
                        }).join(',');
                    } else {
                        return null;
                    }
                };
                this.$watch('searchParams', function () {
                    const query = {};
                    function setQueryParam(name, value) {
                        if (value !== null) {
                            query[name] = value;
                        }
                    }
                    setQueryParam('order',      qOrder());
                    setQueryParam('search',     qSearch());
                    setQueryParam('term',       qTerm());
                    setQueryParam('path',       qPath());
                    setQueryParam('assignment', qAssignment());
                    setQueryParam('group',      qGroup());
                    setQueryParam('author',     qAuthor());
                    setQueryParam('software',   qSoftware());
                    setQueryParam('tags',       qTags());
                    this.$router.replace({ query });
                }, {
                    deep: true,
                    immediate: true,
                });
                this.searchTagsH = ""

                this.searchTagsH += this.searchParams.author && (this.searchParams.author.firstName + " " +this.searchParams.author.lastName)
            },

            async loadGalleryFilters() {
                try {
                    this.galleryFilters = await Galleries.getFilters();
                } catch (err) {
                    console.log('Error loading gallery filters:', err);
                    toastr.error('There was an error while loading the filters. Please try again later.');
                }
            },

            async loadGallerySoftware() {
                try {
                    this.gallerySoftware = await Galleries.getAllSoftware();
                } catch (err) {
                    console.log('Error loading gallery software:', err);
                    toastr.error('There was an error while loading the software. Please try again later.');
                }
            },

            /**
             * The options for some filters depend upon the value of other
             * filters. This method makes sure that the value of those dependent
             * filters is always in a valid state. If the value is no longer
             * allowed (ie. it's not found in the available options), it's value
             * is cleared.
             */
            makeFiltersValid() {
                // NOTE: be very careful about infinite loops here, as this
                // function is set as a watcher for several filter properties.

                // Order must be one of the enum values
                if (!this.orderOptions.find(o => o.id === this.searchParams.order)) {
                    this.searchParams.order = 'latest';
                }

                // Foundation terms do not have a path
                if (this.searchParams.path && this.isTermFoundation(this.searchParams.term)) {
                    this.searchParams.path = '';
                }

                // Groups must exist in the selected term/path
                if (this.searchParams.group) {
                    const exists = this.groupOptions.find(group => {
                        return group.id == this.searchParams.group;
                    });
                    if (!exists) {
                        this.searchParams.group = '';
                    }
                }

                // Assignments must exist in the selected term/path
                if (this.searchParams.condensedAssignment) {
                    const exists = this.condensedAssignmentOptions.find(cao => {
                        return cao.id === this.searchParams.condensedAssignment;
                    });
                    if (!exists) {
                        this.searchParams.condensedAssignment = '';
                    }
                }
            },

            async loadAuthorInFilter() {
                if (!this.searchParams.author) {
                    return;
                }
                try {
                    const user = await Users.getUser(this.searchParams.author.id);
                    this.searchParams.author = this.convertUserToOption(user);
                    this.authorSearchResults = [user];

                } catch (err) {
                    console.log('Error loading author:', err);
                    toastr.error('There was an error while loading the author. Please try again later.');
                }
            },

            async loadSoftwareInFilter() {
                try {
                    const software = await Promise.all(
                        this.searchParams.software.map(s => {
                            return Galleries.getSoftware(s.id);
                        })
                    );
                    this.searchParams.software = software;

                } catch (err) {
                    console.log('Error loading software:', err);
                    toastr.error('There was an error while loading the software. Please try again later.');
                }
            },

            async loadTagsInFilter() {
                try {
                    const tags = await Promise.all(
                        this.searchParams.tags.map(t => Galleries.getTag(t.id))
                    );
                    this.searchParams.tags = tags;

                } catch (err) {
                    console.log('Error loading tags:', err);
                    toastr.error('There was an error while loading the tags. Please try again later.');
                }
            },

            toggleFilters() {
                this.showFilters = !this.showFilters;
            },

            clearFilters() {
                this.searchParams.term = '';
                this.searchParams.path = '';
                this.searchParams.group = '';
                this.searchParams.condensedAssignment = '';
                this.searchParams.author = null; // uses v-select, so null is correct
                this.searchParams.software = [];
                this.searchParams.tags = [];
                this.searchParams.highlightsOnly = false;
            },

            forceSearch() {
                this.searchParams.search = this.searchText;
            },

            searchAuthors(searchTerm, setLoading) {
                setLoading(true);
                const onlyStudents = true;
                Users.searchUsers(searchTerm, onlyStudents).then(users => {
                    this.authorSearchResults = users;
                    setLoading(false);

                }).catch(err => {
                    console.log('Error searching authors:', err);
                    toastr.error('There was an error while loading the authors. Please try again later.');
                    setLoading(false);
                });
            },

            handleEmptyAuthorSearch(ev) {
                // searchAuthors() is only called when the search term is
                // non-empty (this is a known bug in the library); this method
                // should check for an empty search string and clear the results
                // when found.
                if (ev.target.value.length === 0) {
                    this.authorSearchResults = [];
                }
            },

            convertUserToOption(user) {
                let label;
                if (!user.firstName && !user.lastName) {
                    label = 'Anonymous';
                } else if (!!user.firstName !== !!user.lastName) {
                    label = user.firstName || user.lastName;
                } else  {
                    label = user.firstName + ' ' + user.lastName;
                }
                return {
                    id:        user.id,
                    firstName: user.firstName,
                    lastName:  user.lastName,
                    avatar:    user.avatar,
                    group:     user.group,
                    label:     label
                };
            },

            isTermFoundation(termId) {
                if (!termId) {
                    return false;
                }
                const term = this.termOptions.find(t => t.id === termId);
                return !!term && term.type === 'foundation';
            },

            /**
             * Tells gallery grid that its available space has changed, and that
             * it should resize and repack.
             */
            resizeGalleryGrid() {
                this.viewerPanelSize = 'lg';
                if (this.$refs.viewerPanel.clientWidth < 800) {
                    this.viewerPanelSize = 'md';
                }
                this.$nextTick(() => {
                    this.$refs.galleryGrid.updateCellWidth();
                    this.$refs.galleryGrid.repack();
                });
            },

            isGalleryListBottomNearView() {
                const offset = 250;
                const viewBottom = getViewportBottom();
                const listBottom = getBottomPos(this.$refs.galleryGrid.$el);
                return listBottom <= viewBottom + offset;
            },

            shouldLoadMoreGalleries() {
                if (!this.canLoadMoreGalleries) {
                    // There is nothing else to load.
                    return false;

                } else if (this.loadingGalleries) {
                    // We are already loading the next batch.
                    return false;

                } else if (this.galleries.length === 0) {
                    // If the gallery list was just reloaded, the bottom may
                    // still be out of view due to the old rendered galleries;
                    // in this case, we want to fetch the new ones.
                    return true;
                }
                return this.isGalleryListBottomNearView();
            },

            async autoLoadGalleries() {
                while (this.shouldLoadMoreGalleries()) {
                    await this.galleryList.loadMore();
                }
            },

            async reloadGalleryList() {
                this.galleryList = new PaginatedGalleryList(
                    this.gallerySearchParams,
                );
                await this.autoLoadGalleries();
            },
        },

        computed: {
            headerTags() {
                let tags = this.searchParams

                let s = ''
                if(tags.author && tags.author.firstName)
                    s += tags.author.firstName + ' ' +tags.author.lastName

                if(tags.search)
                    s += (s.length > 0 ? ", " : "") +tags.search

                let term = this.termOptions.find(t => t.id === tags.term)
                if(term && term.name)
                    s += (s.length > 0 ? ", " : "") +term.name

                let path = this.pathOptions.find(t => t.id === tags.path)
                if(path && path.name)
                    s += (s.length > 0 ? ", " : "") +path.name

                // let ass = this.assignmentOptions.find(t => t.id === tags.condensedAssignment)
                if(tags.condensedAssignment)
                    s += (s.length > 0 ? ", " : "") +tags.condensedAssignment

                if(tags.software.length > 0)
                    tags.software.forEach(software => {
                        s += (s.length > 0 ? ", " : "") +software.name
                })
                if(tags.tags.length > 0)
                    tags.tags.forEach(software => {
                        s += (s.length > 0 ? ", " : "") +software.name
                })
                let l = this.showFilters ? 78 : 110
                let r = s.substring(0,l)
                return r + (s.length > l ? "...":"")
            },
            galleriesPerRow() {
                if (this.searchParams.highlightsOnly) {
                    return 3;
                }
                if (this.viewerPanelSize === 'lg') {
                    return 8;
                }
                return 5;
            },

            galleriesThresholdSizing() {
                return !this.searchParams.highlightsOnly;
            },

            showGalleries() {
                return !this.loadingInitial && this.galleries.length > 0;
            },

            showLoadingError() {
                // TODO
                return false;
                //return !!this.loadingGalleriesError;
            },

            showLoadingMessage() {
                return this.loadingInitial || this.loadingGalleries;
            },

            showNoGalleriesMessage() {
                return (!this.showLoadingMessage &&
                        !this.showLoadingError &&
                        this.galleries.length === 0);
            },

            gallerySearchParams() {
                return {
                    limit        : 20,
                    order        : this.searchParams.order,
                    term         : this.searchParams.term || null,
                    path         : this.searchParams.path || null,
                    group        : this.searchParams.group || null,
                    assignments  : this.searchParams_assignments,
                    search       : this.searchParams.search || null,

                    // Good ol' v-select deviating from <select> by returning
                    // an object instead of any value attribute.
                    author       : (this.searchParams.author || {}).id,

                    // -1 is used as the default id when the software doesn't
                    // have one. This forces the search to return 0 results
                    // when user enters software that doesn't exist.
                    softwareUsed : this.searchParams.software.map(s => s.id || -1),
                    tags         : this.searchParams.tags.map(t => t.id || -1),

                    // When false, this would limit galleries to those
                    // explicitly NOT highlighted, which isn't what we want; we
                    // want to show ALL galleries in that case, hence the
                    // `|| null`.
                    highlighted  : this.searchParams.highlightsOnly || null,
                };
            },

            galleries() {
                return this.galleryList.galleries;
            },

            canLoadMoreGalleries() {
                return this.galleryList.hasMore();
            },

            loadingGalleries() {
                return this.galleryList.isLoading();
            },

            /**
             * Cache this for performance.
             */
            galleryGridOffsetToEmpty() {
                return this.$refs.galleryGrid.getOffsetToEmpty();
            },

            /**
             * @returns {number[]} - assignment IDs
             */
            searchParams_assignments() {
                const caId = this.searchParams.condensedAssignment;
                const cao = this.condensedAssignmentOptions.find(cao => {
                    return caId === cao.id;
                });
                if (cao) {
                    return cao.assignments.map(ao => ao.id);
                } else {
                    return [];
                }
            },

            orderOptions() {
                return [
                    { id: 'latest',   name: 'Latest'   },
                    { id: 'trending', name: 'Trending' },
                    { id: 'likes',    name: 'Likes'    },
                    { id: 'comments', name: 'Comments' },
                ];
            },

            termOptions() {
                return this.galleryFilters.terms.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                });
            },

            pathOptions() {
                return this.galleryFilters.paths.filter(p => p.id != 9200);
            },

            groupOptions() {
                let groups = this.galleryFilters.groups;
                if (this.searchParams.term) {
                    groups = groups.filter(group => {
                        return group.term === this.searchParams.term;
                    });
                }
                return groups;
            },

            assignmentOptions() {
                let assignments = this.galleryFilters.assignments;
                if (this.searchParams.term) {
                    assignments = assignments.filter(asn => {
                        return asn.term === this.searchParams.term;
                    });
                }
                if (this.searchParams.path) {
                    assignments = assignments.filter(asn => {
                        return asn.path === this.searchParams.path;
                    });
                }
                return assignments.sort((a1, a2) => {
                    a1 = a1.name.toLowerCase();
                    a2 = a2.name.toLowerCase();
                    return a1.localeCompare(a2);
                });
            },

            /**
             * A condensed assignment is a group of assignments that share the
             * same name. The gallery count is summed.
             *
             * @returns {object[]} condensedAssignments
             * @returns {string}   condensedAssignments[].id - NOT the same as an assignment ID
             * @returns {string}   condensedAssignments[].name
             * @returns {number}   condensedAssignments[].galleryCount
             * @returns {object[]} condensedAssignments[].assignments
             */
            condensedAssignmentOptions() {
                let cas = [];
                for (const ao of this.assignmentOptions) {
                    const n = ao.name.trim().toLowerCase();
                    const ca = cas.find(ca => ca.id === n);
                    if (ca) {
                        ca.assignments.push(ao);
                    } else {
                        cas.push({
                            id: n,
                            name: ao.name.trim(),
                            assignments: [ao],
                        });
                    }
                }
                for (const ca of cas) {
                    ca.galleryCount = 0;
                    for (const ao of ca.assignments) {
                        ca.galleryCount += ao.galleryCount;
                    }
                }
                return cas;
            },

            authorOptions() {
                return this.authorSearchResults.map(this.convertUserToOption);
            },

            termSelectClasses() {
                return {
                    'form-control': true,
                    'placeholder': !this.searchParams.term,
                };
            },

            pathSelectClasses() {
                return {
                    'form-control': true,
                    'placeholder': !this.searchParams.path,
                };
            },

            groupSelectClasses() {
                return {
                    'form-control': true,
                    'placeholder': !this.searchParams.group,
                };
            },

            assignmentSelectClasses() {
                return {
                    'form-control': true,
                    'placeholder': !this.searchParams.condensedAssignment,
                };
            },

            authorSelectClasses() {
                return {
                    'v-select-hide-input': !!this.searchParams.author,
                };
            },

            userRoles() {
                return JSON.parse(localStorage.roles);
            },

            canCreateGallery() {
                return !this.userRoles.includes('Recruiter');
            },

            canViewHighlights() {
                return (
                    this.userRoles.includes('Recruiter') ||
                    this.userRoles.includes('OnlineDirector')
                );
            },
        },

        watch: {
            async showGalleries(newValue, oldValue) {
                if (newValue && !oldValue) {
                    await this.$nextTick();
                    if (this.$refs.galleryGrid) {
                        this.$refs.galleryGrid.updateCellWidth();
                    }
                }
            },

            async showFilters() {
                await this.$nextTick();
                this.resizeGalleryGrid();
                await this.autoLoadGalleries();
            },

            searchParams: {
                handler() {
                    if (!this.loadingInitial) {
                        this.makeFiltersValid();
                    }
                },
                deep: true,
            },

            async gallerySearchParams() {
                if (!this.loadingInitial) {
                    await this.reloadGalleryList();
                }
            },

            searchText: RateUtils.debounce(function() {
                this.searchParams.search = this.searchText;
            }, 250),
        },
    }
</script>

<style lang="scss" scoped>
    .panel {
        background-color: #222222 !important;
    }

    .gallery-list-viewer {
        display: flex;
        align-items: flex-start;
    }

    .viewer-panel {
        flex: 1;
        margin-right: 20px;
    }

    .gallery-controls {
        display: flex;
        padding: 20px 20px 25px 20px;

        .create-gallery {
            margin-right: 20px;
        }
        form {
            flex: 1;
            max-width: 250px;
            margin: 0 auto 0 0;
            position: relative;
            top: -1px;
        }
        .form-control {
            height: 42px;
            border-bottom-color: #8e8e8e !important;
        }
        input.form-control:focus {
            border-bottom-color: #fff !important;
        }
        input {
            min-width: 100px;
        }
        .sort-controls {
            display: flex;
            margin-right: 20px;
            align-items: center;
            label {
                margin: 0 8px 5px 30px;
                white-space: nowrap;
            }
            select {
                width: 120px;
            }
        }
    }

    .no-galleries-container,
    .error-container,
    .loading-container {
        display: table;
        height: 100px;
        width: 100%;
        text-align: center;
        position: relative;
        top: -10px;
    }

    .no-galleries-container p,
    .error-container p,
    .loading-container p {
        display: table-cell;
        font-style: italic;
        vertical-align: middle;
    }

    .gallery-grid {
        margin: 0 20px 20px 20px;
    }

    .filters-panel {
        flex: 0 0 275px;
        max-width: 275px;
        overflow-wrap: break-word;
        word-wrap: break-word;

        .panel-body {
            padding: 20px;
        }

        .form-group {
            margin-top: 30px;
        }
        .form-group:first-child {
            margin-top: 0;
        }
        .form-group:last-child {
            margin-bottom: 0;
        }

        .recruiter-visibility {
            label {
                display: block;
                top: 0;
            }
            input {
                margin: 0;
            }
        }

        select.placeholder {
            color: #b0b0b0;
        }
    }
</style>

<style lang="scss">
    .gallery-list-viewer {
        .author-group {
            .v-select {
                .selected-tag {
                    color: #ededed;
                }
                .dropdown-toggle {
                    border: none;
                }
                .dropdown-menu {
                    background-color: #363636;
                    border: 1px solid #666;
                    padding: 0;
                    overflow-y: auto;
                    max-height: 200px;
                    li a {
                        color: #ebebeb;
                    }
                    li.highlight a {
                        color: white;
                    }
                }
                .clear {
                    color: #555;
                }
                .open-indicator::before {
                    border-color: #b0b0b0;
                }
                .spinner {
                    border-top-color: rgba(255, 255, 255, 0.2);
                    border-right-color: rgba(255, 255, 255, 0.2);
                    border-bottom-color: rgba(255, 255, 255, 0.2);
                    border-left-color: rgba(51, 204, 255, 0.65);
                }
            }
        }
    }
    .gallery-software-list-editor {
        .software-wrapper {
            .software-select {
                .software-list > li {
                    width: 33.3%;
                }
            }
        }
    }

    .headerTags {
        text-transform: capitalize;
    }
</style>



// WEBPACK FOOTER //
// GalleryListViewer.vue?a9bca5dc